import { useContext, useMemo, useState } from "react";
import { XIcon } from "lucide-react";
import { Button } from "@/common/components/ui/button.tsx";
import { Skeleton } from "@/common/components/ui/skeleton.tsx";
import { Alert, AlertDescription } from "@/common/components/ui/alert.tsx";
import { useQuery } from "@apollo/client/react";
import { cn } from "@/common/lib/utils/utils.ts";
import { GetLedgerAttributesDocument } from "@/graphql/definitions.ts";
import {
  LedgerSearchParamsContext,
  type LedgerSearchParams,
} from "@/common/providers/ledger-search-params-provider";
import {
  Combobox,
  type ComboboxOption,
} from "@/common/components/ui/combobox.tsx";
import { getIndentLevel, serializePayeeFilter } from "./utils.ts";
import { generateAllAccountPaths } from "@/common/lib/utils/account-utils.ts";
import { useTranslations } from "@/common/hooks/use-translations.ts";

/**
 * A suggestion is either the literal text it offers, or a value plus the text
 * a reader would type to find it when the two differ.
 */
export type SearchControlItem = string | { value: string; searchText: string };

interface SearchControlComboboxProps {
  items: SearchControlItem[];
  selected: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  allowCustom?: boolean;
  hierarchical?: boolean;
  triggerOn?: "change" | "blur";
}

/**
 * SearchControlCombobox component - searchable combobox for searchParams
 */
export const SearchControlCombobox = ({
  items,
  selected,
  onChange,
  placeholder,
  className,
  allowCustom = true,
  hierarchical = false,
  triggerOn = "change",
}: SearchControlComboboxProps) => {
  const { t } = useTranslations();
  const defaultPlaceholder = placeholder || t("component.combobox.placeholder");
  // Convert items to ComboboxOption format
  const options: ComboboxOption[] = useMemo(() => {
    return items.map((item) => {
      const value = typeof item === "string" ? item : item.value;
      return {
        value,
        label: value,
        searchText: typeof item === "string" ? undefined : item.searchText,
        indent: hierarchical ? getIndentLevel(value) : 0,
      };
    });
  }, [items, hierarchical]);

  return (
    <div className={cn("", className)}>
      <Combobox
        options={options}
        value={selected}
        onValueChange={onChange}
        placeholder={defaultPlaceholder}
        allowCustom={allowCustom}
        emptyText={t("component.combobox.noMatchesFound")}
        triggerOn={triggerOn}
      />
    </div>
  );
};

export const LedgerSearchControls = ({
  ledgerId,
  layout = "inline",
}: {
  ledgerId: string;
  /** `stack` stacks the fields for narrow filter sheets. */
  layout?: "inline" | "stack";
}) => {
  const { t } = useTranslations();
  const { searchParams, setSearchParams } = useContext(
    LedgerSearchParamsContext,
  );
  const { data, loading, error } = useQuery(GetLedgerAttributesDocument, {
    variables: { ledgerId },
  });

  // A same-ledger refresh (e.g. the cache eviction after a write) must not
  // replace already-rendered controls with Skeletons: that unmounts the
  // comboboxes and discards their uncommitted drafts (w4/1077). Retain the
  // last healthy suggestions for this ledger while it reloads.
  // React's store-from-previous-render pattern, keyed to this ledger.
  const [retained, setRetained] = useState<{
    ledgerId: string;
    data: typeof data;
  } | null>(null);
  if (data && (retained?.data !== data || retained.ledgerId !== ledgerId)) {
    setRetained({ ledgerId, data });
  }
  const shownData =
    data ?? (retained?.ledgerId === ledgerId ? retained.data : undefined);

  const handleFilterChange = (
    filterType: keyof LedgerSearchParams,
    value: string,
  ) => {
    setSearchParams({ [filterType]: value });
  };

  const handleClearAll = () => {
    setSearchParams({
      account: "",
      filter: "",
      time: "",
    });
  };

  const hasActiveFilters = Object.values(searchParams).some(
    (value) => value !== "",
  );
  const isStack = layout === "stack";

  // Loading state (initial load only; a refresh keeps the controls above)
  if (loading && !shownData && !error) {
    return (
      <div
        className={cn(
          isStack ? "flex w-full flex-col gap-3" : "flex items-center gap-2",
        )}
      >
        <Skeleton className={cn("h-9", isStack ? "w-full" : "w-24")} />
        <Skeleton className={cn("h-9", isStack ? "w-full" : "w-24")} />
        <Skeleton className={cn("h-9", isStack ? "w-full" : "w-24")} />
      </div>
    );
  }

  // No data state
  if (!shownData?.getLedgerAttributes && !error) {
    return null;
  }

  // Suggestions are optional; applied values and manual edits belong to the
  // provider. A failed read must not expose retained or partial suggestions.
  const {
    accounts = [],
    tags = [],
    years = [],
    links = [],
    payees = [],
  } = (error ? undefined : shownData?.getLedgerAttributes) ?? {};

  // Generate all partial account paths and sort hierarchically
  const sortedAccounts = generateAllAccountPaths(accounts);

  const fql_filter_suggestions: SearchControlItem[] = [
    ...tags.map((tag) => `#${tag}`),
    ...links.map((link) => `^${link}`),
    // The suggestion offers the escaped FQL expression, but a reader looks for
    // the payee as it is printed in their journal, punctuation and all.
    ...payees.map((payee) => ({
      value: serializePayeeFilter(payee),
      searchText: payee,
    })),
  ];

  const comboboxClass = isStack ? "w-full min-w-0" : undefined;

  const controls = (
    <div
      className={cn(
        isStack ? "flex w-full flex-col gap-3" : "flex items-center gap-3",
      )}
    >
      {hasActiveFilters && (
        <Button
          variant="ghost"
          size="sm"
          onClick={handleClearAll}
          className={cn(
            "h-9 px-2 text-muted-foreground hover:text-foreground",
            isStack && "self-start",
          )}
        >
          <XIcon className="h-3 w-3 mr-1" />
          {t("component.searchControls.clearAll")}
        </Button>
      )}

      <div
        className={cn(
          isStack ? "flex w-full flex-col gap-3" : "flex items-center gap-2",
        )}
      >
        <SearchControlCombobox
          items={years}
          selected={searchParams.time}
          onChange={(value) => handleFilterChange("time", value)}
          placeholder={t("component.searchControls.time")}
          className={cn(comboboxClass, !isStack && "min-w-[160px]")}
          allowCustom={true}
          hierarchical={false}
          triggerOn="blur"
        />

        <SearchControlCombobox
          items={sortedAccounts}
          selected={searchParams.account}
          onChange={(value) => handleFilterChange("account", value)}
          placeholder={t("component.searchControls.account")}
          className={cn(comboboxClass, !isStack && "min-w-[180px]")}
          allowCustom={true}
          hierarchical={true}
          triggerOn="blur"
        />

        <SearchControlCombobox
          items={fql_filter_suggestions}
          selected={searchParams.filter}
          onChange={(value) => handleFilterChange("filter", value)}
          placeholder={t("component.searchControls.filterByTagPayee")}
          className={cn(comboboxClass, !isStack && "min-w-[180px]")}
          allowCustom={true}
          hierarchical={false}
          triggerOn="blur"
        />
      </div>
    </div>
  );

  if (!error) return controls;

  return (
    <div className={cn("flex flex-col", isStack ? "w-full gap-3" : "gap-1")}>
      <Alert className="w-auto border-0 bg-transparent p-0">
        <AlertDescription className="text-xs leading-4">
          {t("component.searchControls.failedToLoad")}
        </AlertDescription>
      </Alert>
      {controls}
    </div>
  );
};
